/**
 * ReadyBriefsQueue — emails with a prepared brief the AM still needs to
 * review and start (monitored mailboxes, their own mailbox, or a brief they
 * prepared by opening the email). Distinct from WaitingQueue (in-flight HITL).
 * Shared by ora and the add-in.
 *
 * The card matches the current-email strip: white panel, recommended label,
 * Review & Approve. Ignore sits top-right. Open Email shares the recommended
 * label's row, right-aligned above Review & Approve, so the card spends no
 * extra line on it.
 *
 * NOTHING renders when there is no card to show — no heading, no "nothing
 * here" box, no "Checking…" before the first read (story 28633: an empty
 * section is noise in a 440px pane). Text uses the pane's three styles only
 * (`paneTypography`).
 */

import React from "react";
import { CONTEXT_PRIMARY_ACTION } from "../emailContextState";
import {
  PRIMARY_ACTION_BUTTON_CLASS,
  TYPE_BODY,
  TYPE_LABEL,
  TYPE_TITLE,
} from "../paneTypography";
import type { ReadyBriefItem } from "../types";
import { workflowDisplayName } from "../workflowDisplayName";
import { OpenEmailLink } from "./OpenEmailLink";

export const READY_BRIEFS_HEADING = "Ready to start";

export interface ReadyBriefsQueueProps {
  items: ReadyBriefItem[];
  /** RFC 5322 id of the email the pane is currently on — omitted from this list. */
  currentMessageId?: string | null;
  /**
   * The REGISTERED name for a recommended workflow id (the host's catalog),
   * or null when the host cannot name it. Omitted or null falls back to
   * `workflowDisplayName` — never a title-cased id of this component's own.
   */
  workflowLabel?: (workflowId: string) => string | null;
  onOpenBrief: (item: ReadyBriefItem) => void;
  onOpenInOutlook?: (item: ReadyBriefItem) => void;
  /** Hide this brief from THIS AM's list. Does not delete the analysis. */
  onIgnore?: (item: ReadyBriefItem) => void;
}

function subjectLine(item: ReadyBriefItem): string {
  if (item.subject) return item.subject;
  if (item.client_name && item.policy_number) {
    return `${item.client_name} · ${item.policy_number}`;
  }
  if (item.client_name) return item.client_name;
  return "Brief ready";
}

export function ReadyBriefsQueue({
  items,
  currentMessageId,
  workflowLabel,
  onOpenBrief,
  onOpenInOutlook,
  onIgnore,
}: ReadyBriefsQueueProps): React.ReactElement | null {
  const current = (currentMessageId || "").trim();
  const visible = current
    ? items.filter((item) => item.internet_message_id !== current)
    : items;

  if (visible.length === 0) return null;

  const recommendedLabel = (workflowId: string): string =>
    workflowLabel?.(workflowId) ?? workflowDisplayName(workflowId) ?? workflowId;

  return (
    <section
      className="flex w-full flex-col gap-2"
      data-testid="ready-briefs-queue"
      data-item-count={visible.length}
    >
      <h3
        data-testid="ready-briefs-heading"
        // Sticky, like WaitingQueue's headings: stays put while its cards scroll.
        className={`sticky top-0 z-10 mb-1 bg-primary-gray-light-50 py-1 ${TYPE_LABEL} text-primary-gray-light-500`}
      >
        {READY_BRIEFS_HEADING} · {visible.length}
      </h3>
      <ul
        aria-label={READY_BRIEFS_HEADING}
        className="flex list-none flex-col gap-2 p-0"
      >
        {visible.map((item) => (
          <li key={item.internet_message_id} className="list-none">
            <div
              className="flex w-full flex-col gap-2 rounded-lg border border-primary-gray-light-300 bg-base-white px-3 py-3"
              data-testid="ready-brief-row"
              data-message-id={item.internet_message_id}
            >
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className={`${TYPE_TITLE} text-primary-gray-light-900`}>
                    {subjectLine(item)}
                  </p>
                  {item.sender ? (
                    <p className={`${TYPE_BODY} text-primary-gray-light-600`}>
                      {item.sender}
                    </p>
                  ) : null}
                  {item.client_name || item.policy_number ? (
                    <p className={`${TYPE_BODY} text-primary-gray-light-600`}>
                      {[item.client_name, item.policy_number]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  ) : null}
                </div>
                {onIgnore ? (
                  <button
                    type="button"
                    onClick={() => onIgnore(item)}
                    data-testid="ready-brief-ignore"
                    className={`flex-shrink-0 ${TYPE_BODY} text-primary-gray-light-500 hover:text-primary-gray-light-800`}
                  >
                    Ignore
                  </button>
                ) : null}
              </div>

              {item.recommended_workflow_id ||
              (item.graph_message_id && onOpenInOutlook) ? (
                <div
                  className="flex items-center gap-2"
                  data-testid="ready-brief-recommended-row"
                >
                  {item.recommended_workflow_id ? (
                    <p className={`min-w-0 flex-1 ${TYPE_LABEL} text-primary-gray-light-500`}>
                      Recommended · {recommendedLabel(item.recommended_workflow_id)}
                    </p>
                  ) : null}
                  {item.graph_message_id && onOpenInOutlook ? (
                    <OpenEmailLink
                      onClick={() => onOpenInOutlook(item)}
                      testId="ready-brief-open-outlook"
                      className="ml-auto"
                    />
                  ) : null}
                </div>
              ) : null}

              <button
                type="button"
                onClick={() => onOpenBrief(item)}
                data-testid="ready-brief-open"
                className={PRIMARY_ACTION_BUTTON_CLASS}
              >
                {CONTEXT_PRIMARY_ACTION.brief_ready}
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default ReadyBriefsQueue;
